import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Percent, Pencil, Trash2, Plus, Users } from "lucide-react";

const EMPTY = { name: "", threshold: 3, discount_pct: 10, active: true };

export default function AdminStripeDiscounts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // full row or null
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/stripe-discounts");
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            name: row.name || "",
            threshold: row.threshold,
            discount_pct: row.discount_pct,
            active: row.active,
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim() || null,
                threshold: Number(form.threshold),
                discount_pct: Number(form.discount_pct),
                active: form.active,
            };
            if (editing) {
                await api.patch(`/admin/stripe-discounts/${editing.id}`, payload);
                toast.success("Discount tier updated");
            } else {
                await api.post("/admin/stripe-discounts", payload);
                toast.success("Discount tier created (Stripe coupon minted)");
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (
            !window.confirm(
                `Delete "${row.name}"? Members currently at this tier will fall back to the next matching threshold.`
            )
        )
            return;
        try {
            await api.delete(`/admin/stripe-discounts/${row.id}`);
            toast.success("Tier removed");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Admin"
                title="Stripe discount tiers"
                description="Threshold-based discounts automatically applied to primary members when their referral count crosses each tier. Rename to reflect the referral threshold and % off — the label the member sees."
                testid="admin-stripe-discounts-header"
                action={
                    <Button
                        onClick={openCreate}
                        data-testid="stripe-discount-new-btn"
                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-cream h-11 px-6"
                    >
                        <Plus size={16} className="mr-2" />
                        New tier
                    </Button>
                }
            />

            <div className="px-6 lg:px-12 py-8">
                <div
                    data-testid="stripe-discount-table"
                    className="rounded-2xl bg-white border border-navy-100 overflow-hidden"
                >
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-cream">
                                <TableHead>Name</TableHead>
                                <TableHead>Threshold</TableHead>
                                <TableHead>% off</TableHead>
                                <TableHead>Stripe coupon</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8 text-navy-500">
                                        Loading tiers…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-10 text-navy-500">
                                        <Percent className="mx-auto mb-2 text-navy-300" />
                                        No discount tiers yet. Create the first one to activate referral rewards.
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid={`stripe-discount-row-${r.id}`}>
                                    <TableCell className="font-medium text-navy-700">{r.name}</TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center gap-1 text-navy-500">
                                            <Users size={12} />
                                            {r.threshold} secondary members
                                        </span>
                                    </TableCell>
                                    <TableCell className="font-mono text-navy-700">
                                        {r.discount_pct}%
                                    </TableCell>
                                    <TableCell className="font-mono text-xs text-navy-500">
                                        {r.stripe_coupon_id || "—"}
                                    </TableCell>
                                    <TableCell>
                                        {r.active ? (
                                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                Active
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-navy-100 text-navy-500">
                                                Paused
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            data-testid={`stripe-discount-edit-${r.id}`}
                                            onClick={() => openEdit(r)}
                                            className="h-8"
                                        >
                                            <Pencil size={14} />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            data-testid={`stripe-discount-del-${r.id}`}
                                            onClick={() => remove(r)}
                                            className="h-8 text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="stripe-discount-dialog"
                    className="max-w-md bg-white"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-navy-700">
                            {editing ? "Edit discount tier" : "New discount tier"}
                        </DialogTitle>
                        <DialogDescription>
                            Saving mints (or updates) a matching coupon on Stripe automatically.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div>
                            <Label>Name (optional)</Label>
                            <Input
                                data-testid="stripe-discount-name"
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. 3 Referrals · 10% off"
                                className="mt-1 h-11"
                            />
                            <div className="text-[11px] text-navy-400 mt-1">
                                Leave blank to auto-generate from threshold + %.
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Threshold (referrals)</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={1000}
                                    required
                                    data-testid="stripe-discount-threshold"
                                    value={form.threshold}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, threshold: e.target.value }))
                                    }
                                    className="mt-1 h-11"
                                />
                            </div>
                            <div>
                                <Label>% off</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={100}
                                    required
                                    data-testid="stripe-discount-pct"
                                    value={form.discount_pct}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, discount_pct: e.target.value }))
                                    }
                                    className="mt-1 h-11"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between rounded-xl border border-navy-100 bg-cream px-4 py-3">
                            <div className="text-sm text-navy-700">Active</div>
                            <Switch
                                data-testid="stripe-discount-active"
                                checked={form.active}
                                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setOpen(false)}
                                className="rounded-full"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                data-testid="stripe-discount-save"
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-cream"
                            >
                                {saving ? "Saving…" : editing ? "Update" : "Create tier"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
